import { Feature, FeatureCollection } from "geojson";
import gjv from "geojson-validation";

import { extname } from "fond/fileValidation";

/** Whether the file's extension marks it as GeoJSON. */
export const isGeoJsonFile = (file: File): boolean => extname(file.name)?.toLowerCase() === "geojson";

/** Named CRSs that legacy GeoJSON may declare which already mean WGS84 longitude/latitude. */
const WGS84_CRS_NAME = /(?:CRS84|EPSG::?4326)$/i;

const readText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

const isWgs84 = (crs: unknown): boolean => {
  if (crs === undefined || crs === null) return true;

  const name = (crs as { properties?: { name?: unknown } }).properties?.name;
  return typeof name === "string" && WGS84_CRS_NAME.test(name);
};

/**
 * Reads a GeoJSON file as a WGS84 feature collection.
 *
 * Resolves undefined for anything that needs GDAL instead: content that does not
 * parse, a declared CRS other than WGS84, or a document that is neither a feature
 * collection nor a feature.
 */
export const readGeoJsonFile = async (file: File): Promise<FeatureCollection | undefined> => {
  let json: unknown;
  try {
    json = JSON.parse(await readText(file));
  } catch {
    return undefined;
  }

  if (typeof json !== "object" || json === null || !isWgs84((json as { crs?: unknown }).crs)) return undefined;
  if (gjv.isFeatureCollection(json)) return json as FeatureCollection;
  if (gjv.isFeature(json)) return { type: "FeatureCollection", features: [json as Feature] };

  return undefined;
};
